import { FortuneSheet, FortuneCell } from '../types';

export interface RowCol {
    r: number;
    c: number;
}

/** Ctrl+End: the last used cell — the max row/col over every cell
 *  that carries a value OR its own explicit formatting (a formatted-but-blank
 *  cell still counts, matching Excel's own "used range" notion). Empty sheet
 *  -> {r:0, c:0} (top-left, same as Ctrl+Home). */
export const lastUsedCell = (data: FortuneSheet['celldata']): RowCol => {
    let maxR = 0;
    let maxC = 0;
    (data ?? []).forEach(({ r, c, v }) => {
        if (v == null) return;
        const cell = v as FortuneCell;
        const hasContent = cell.v != null || cell.f != null || Boolean(cell.ct?.fa && cell.ct.fa !== 'General');
        if (!hasContent) return;
        maxR = Math.max(maxR, r);
        maxC = Math.max(maxC, c);
    });
    return { r: maxR, c: maxC };
};

/** Ctrl+Home: the first unfrozen cell — {r:0,c:0} with no freeze, or
 *  just past a frozen row/column/both pane. Fortune core (1.0.4)'s own
 *  frozenTofreezen normalises "row"/"column"/"both" to "rangeRow"/
 *  "rangeColumn"/"rangeBoth" and THEN defaults a missing `range` to
 *  {row_focus:0, column_focus:0} unconditionally — no type gets special
 *  no-freeze treatment for a missing range. */
export const firstUnfrozenCell = (frozen: FortuneSheet['frozen'] | undefined): RowCol => {
    if (!frozen) return { r: 0, c: 0 };
    const row_focus = frozen.range?.row_focus ?? 0;
    const column_focus = frozen.range?.column_focus ?? 0;
    if (frozen.type === 'row' || frozen.type === 'rangeRow') return { r: row_focus + 1, c: 0 };
    if (frozen.type === 'column' || frozen.type === 'rangeColumn') return { r: 0, c: column_focus + 1 };
    return { r: row_focus + 1, c: column_focus + 1 };
};

export interface SelectionRange {
    row: [number, number];
    column: [number, number];
}

/** Ctrl+Space / Shift+Space: expands `sel` to the full column(s)
 *  ('col' mode) or row(s) ('row' mode) it spans — the OTHER axis becomes the
 *  sheet's full extent, the given axis is left as-is. Applying 'row' to a
 *  selection that's already a full-column selection (from a prior Ctrl+Space)
 *  extends it to the whole sheet, matching Excel's Ctrl+Space then Shift+Space. */
export const fullRowColRange = (sel: SelectionRange, dims: { rows: number; cols: number }, mode: 'row' | 'col'): SelectionRange =>
    mode === 'col' ? { row: [0, dims.rows - 1], column: sel.column } : { row: sel.row, column: [0, dims.cols - 1] };

interface SheetOrderInfo {
    id?: string;
    order?: number;
    hide?: number;
}

/** The next/previous VISIBLE sheet in tab order (hide===1 skipped),
 *  or null past either end — Excel's sheet-switch shortcuts don't wrap. */
export const nextSheetId = (sheets: SheetOrderInfo[], currentId: string | undefined, dir: 'next' | 'prev'): string | null => {
    const visible = sheets.filter((s) => s.hide !== 1).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const index = visible.findIndex((s) => s.id === currentId);
    if (index === -1) return null;
    const targetIndex = dir === 'next' ? index + 1 : index - 1;
    if (targetIndex < 0 || targetIndex >= visible.length) return null;
    return visible[targetIndex].id ?? null;
};

/** The fill-handle double-click fill-down extent: the contiguous non-empty
 *  run below `sel`'s BLOCK (every row/column it spans, not just its
 *  top-left cell) in the LEFT-adjacent column, falling back to the RIGHT
 *  column when the left one is empty (mirrors Excel: fill down follows
 *  whichever neighbour column actually has data, and fills the whole
 *  source block — every column, series continuing from every one of its
 *  rows — not just its first cell). null when both neighbours are empty. */
export const fillDownExtent = (data: FortuneSheet['celldata'], sel: SelectionRange): SelectionRange | null => {
    // One pass to index populated cells, so contiguousRun's per-row walk is
    // O(1) lookups instead of an O(n) rescan of the whole sheet per row.
    const populated = new Set<string>();
    (data ?? []).forEach(({ r, c, v }) => {
        if (v != null && (v as FortuneCell).v != null) populated.add(`${r}_${c}`);
    });
    const valueAt = (r: number, c: number): boolean => populated.has(`${r}_${c}`);

    // Measured from the BLOCK's own bottom edge (sel.row[1]), not its top —
    // a multi-row source's fill-down extent is how far the neighbour column
    // runs past where the block itself ends.
    const contiguousRun = (col: number): number => {
        let last = sel.row[1];
        let r = sel.row[1] + 1;
        while (valueAt(r, col)) {
            last = r;
            r += 1;
        }
        return last;
    };

    const leftCol = sel.column[0] - 1;
    const leftEnd = leftCol >= 0 ? contiguousRun(leftCol) : sel.row[1];
    if (leftEnd > sel.row[1]) return { row: [sel.row[1] + 1, leftEnd], column: [sel.column[0], sel.column[1]] };

    const rightCol = sel.column[1] + 1;
    const rightEnd = contiguousRun(rightCol);
    if (rightEnd > sel.row[1]) return { row: [sel.row[1] + 1, rightEnd], column: [sel.column[0], sel.column[1]] };

    return null;
};

/** Fortune only writes `sheet.row`/`sheet.column` on
 *  insert/delete row-col (`core:66055`, `core:66688`) — a paste that grows the
 *  grid (`expandRowsAndColumns`) leaves them at their creation value while the
 *  runtime `data` matrix keeps growing. Bounds derived from `row`/`column`
 *  alone go stale on a grown sheet; take the max with the live `data` extent
 *  instead. Shared by every bounds computation below Go To, Ctrl+Space and
 *  formula point mode — including the per-keystroke point-mode path, so column
 *  width reads only the first row (`core:67340`'s `expandRowsAndColumns` keeps
 *  every row the same width) rather than an O(rows) scan of the whole sheet. */
export const liveDims = (sheet: Pick<FortuneSheet, 'row' | 'column' | 'data'> | undefined): { rows: number; cols: number } => {
    const data = sheet?.data ?? [];
    return {
        rows: Math.max(sheet?.row ?? 1, data.length),
        cols: Math.max(sheet?.column ?? 1, data[0]?.length ?? 0),
    };
};

/** The REAL used extent of the dense `data` matrix, based on cell content
 *  (value or formula) only — ignoring format-only cells, unlike
 *  `lastUsedCell` (Ctrl+End's own notion of "used", which also counts a
 *  cell carrying just its own explicit formatting — matching Excel there).
 *  Format Cells' "format a blank within the data extent" gate needs this
 *  narrower notion instead: `liveDims` is the PADDED grid (≥50 rows), so
 *  gating on it formatted every blank in a whole-column selection and could
 *  move Ctrl+End's own target to the padding. Gating on content alone keeps
 *  the extent it reads from stable — applying a format never grows it for a
 *  later call, so a whole-column/whole-sheet selection stays cheap and
 *  Ctrl+End's target never moves just from formatting a blank. Empty sheet
 *  -> {rows:0, cols:0} (no blank gets the format at all). */
export const usedContentDims = (sheet: Pick<FortuneSheet, 'data'> | undefined): { rows: number; cols: number } => {
    let maxR = -1;
    let maxC = -1;
    (sheet?.data ?? []).forEach((row, r) => {
        (row ?? []).forEach((cell, c) => {
            if (cell == null || (cell.v == null && cell.f == null)) return;
            if (r > maxR) maxR = r;
            if (c > maxC) maxC = c;
        });
    });
    return { rows: maxR + 1, cols: maxC + 1 };
};
